import { saveInstanceId, getSavedInstanceId } from "./authService";
import { ROUTES } from "../components/shared/constant";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const INSTANCE_STORAGE_KEY = "sky-selected-instance-id";

// Routes where UUID path segments are resource IDs, NOT instance IDs
const RESOURCE_ID_ROUTES = ["/surveys/", "/locations/", "/operators/", "/owners/", "/asa/"];

// CRM route patterns whose LAST segment is a RECORD id, not an instance id — an
// explicit per-route allowlist, one entry per registered route.
//
// The harvest below takes the last path segment as the instance id when it is a
// UUID. On every pattern listed here that last segment is a record id (a person,
// a report, an event, a webhook subscription ...), so a cold URL of exactly this
// shape must NOT be harvested — otherwise opening it overwrites the operator's
// selected instance in localStorage with the record's id.
//
// WHY PER ROUTE, not per entity or per subtree. Two earlier rules could not
// express every shape:
//   - a segment-COUNT rule ("/crm/<entity>/<recordId>", 3 segments) misses the
//     nested /crm/reports/builder/:reportId and /crm/events/:source/:eventId;
//   - a SUBTREE rule ("everything under /crm/reports/") is only sound while no
//     route under the prefix ends in an instance id — and /crm/events cannot use
//     it, because both the events list and its detail legitimately take one.
// Matching the URL against the REGISTERED pattern classifies each route by its
// own shape: /crm/events/:source/:eventId (4 segments) is a record id, while the
// in-app form /crm/events/<source>/<eventId>/<instanceId> (5 segments) matches
// no entry here and keeps harvesting its instance id.
//
// DERIVED FROM THE ROUTE REGISTRY. The list is exactly the /crm patterns in
// src/routes/appRoutes.ts whose path — after dropping a trailing optional
// ":instanceid?" — ends in a dynamic segment. A generated guard in
// src/routes/__tests__/crmDeepLinkRoutes.test.ts recomputes that set from the
// real route table and fails if this list drifts from it in either direction,
// so a new CRM detail route cannot ship unlisted.
export const CRM_RECORD_ID_TAIL_ROUTES: readonly string[] = [
  ROUTES.CRM_PERSON_DETAIL,
  ROUTES.CRM_RESIDENT_DETAIL,
  ROUTES.CRM_REFERRER_PERSON_DETAIL,
  ROUTES.CRM_ORGANIZATION_DETAIL,
  ROUTES.CRM_REPORT_BUILDER_EDIT,
  ROUTES.CRM_REPORT_PACKAGE_DETAIL,
  ROUTES.CRM_REPORT_DETAIL,
  ROUTES.CRM_EVENT_DETAIL,
  ROUTES.CRM_COMMUNITY_EVENT_DETAIL,
  `${ROUTES.CRM_COMMUNITY_SETUP}/:communityId`,
  `${ROUTES.CRM_API_TOKEN_EDIT}/:tokenId`,
  `${ROUTES.CRM_WEBHOOK_EDIT}/:subscriptionId`,
  `${ROUTES.CRM_DELIVERY_DESTINATION_EDIT}/:id`,
];

const RECORD_ID_TAIL_SEGMENTS = CRM_RECORD_ID_TAIL_ROUTES.map((p) => p.split("/").filter(Boolean));

/**
 * True when `pathSegments` is EXACTLY one of the record-id-tail patterns: same
 * segment count, every static segment equal, a ":param" matching any segment.
 * Exported for the route-registry guard.
 */
export function isCrmRecordIdTail(pathSegments: string[]): boolean {
  return RECORD_ID_TAIL_SEGMENTS.some(
    (pattern) =>
      pattern.length === pathSegments.length &&
      pattern.every((seg, i) => seg.startsWith(":") || seg === pathSegments[i]),
  );
}

export function clearStoredInstanceId(): void {
  try {
    localStorage.removeItem(INSTANCE_STORAGE_KEY);
  } catch {
    // Ignore localStorage errors (e.g., private browsing mode)
  }
}

export function getInstanceIdFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  const queryInstanceId = urlParams.get("instanceid") || urlParams.get("instanceId");
  if (queryInstanceId && UUID_REGEX.test(queryInstanceId)) {
    saveInstanceId(queryInstanceId);
    return queryInstanceId;
  }

  // Check if we're on a route where the UUID is a resource ID (not instance ID)
  const pathname = window.location.pathname;
  const pathSegments = pathname.split("/").filter(Boolean);
  const isResourceIdRoute =
    RESOURCE_ID_ROUTES.some((route) => pathname.startsWith(route)) ||
    isCrmRecordIdTail(pathSegments);

  // Only extract instance ID from path if not on a resource ID route
  if (!isResourceIdRoute) {
    if (pathSegments.length > 0) {
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (UUID_REGEX.test(lastSegment)) {
        saveInstanceId(lastSegment);
        return lastSegment;
      }
    }
  }

  const storedInstanceId = getSavedInstanceId();
  if (storedInstanceId && UUID_REGEX.test(storedInstanceId)) {
    return storedInstanceId;
  }

  return null;
}

export function getInstanceParam(): string {
  const instanceId = getInstanceIdFromUrl();
  return instanceId ? `instanceId=${instanceId}` : "";
}
